import {ReactNode, useEffect, useState} from "react"
import {usePage, Link} from "@inertiajs/react"
import {Calendar, LogOut, Menu, Users} from "lucide-react"
import {
    SidebarGroup,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
} from "@/components/ui/sidebar"
import {Separator} from "@/components/ui/separator.tsx";
import Flash from "@/components/Flash.tsx";
import {Toaster} from "sonner";

interface Props {
    children: ReactNode
    header?: ReactNode
    onSidebarToggle?: (open: boolean) => void
}

export default function AuthorizedLayout({ children, header, onSidebarToggle }: Props) {
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
        if (typeof window === "undefined") return true;
        return window.innerWidth >= 768;
    });
    const [windowWidth, setWindowWidth] = useState<number>(() => (typeof window === "undefined" ? 1024 : window.innerWidth));
    const page = usePage() as any
    const currentPath =
        page?.url ?? (typeof window !== "undefined" ? window.location.pathname : "")

    useEffect(() => {
        document.title = "Ordinacija Kardašević"
    }, [])

    useEffect(() => {
        const onResize = () => {
            const w = window.innerWidth;
            setWindowWidth(w);
            if (w < 768) {
                setSidebarOpen(false);
            } else {
                setSidebarOpen(true);
            }
        };

        if (typeof window !== "undefined") {
            window.addEventListener("resize", onResize);
            onResize();
        }

        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", onResize);
            }
        };
    }, []);

    const navItems = [
        { title: "Raspored", url: "/events", icon: Calendar },
        { title: "Pacijenti", url: "/patients", icon: Users },
        { title: "Lekari", url: "/doctors", icon: Users },
        { title: "Sestre", url: "/nurses", icon: Users },
    ]

    return (
        <div className="min-h-screen bg-muted/50 relative">
            <Toaster
                position="top-center"
                richColors
                expand
            />
            <div
                className="fixed top-0 left-0 right-0 h-14 bg-background border-b flex items-center px-4 md:px-6 z-50 shadow-sm"
                role="banner"
            >
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => {
                            setSidebarOpen((prev) => {
                                const next = !prev
                                onSidebarToggle?.(next)
                                return next
                            })
                        }}
                        className="p-2 rounded-md hover:bg-muted transition"
                        aria-label="Toggle navigation"
                    >
                        <Menu className="h-5 w-5" />
                    </button>

                    <Link href="/" className="text-l md:text-xl font-semibold whitespace-nowrap text-foreground">
                        Ordinacija Kardašević
                    </Link>
                </div>

                <div className="mx-4 flex-1">
                    <div className="w-full flex items-center justify-center md:justify-start">
                        {header}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Flash />
                </div>
            </div>

            {sidebarOpen && (
                <aside
                    className={`fixed top-14 left-0 w-64 h-[calc(100vh-3.5rem)] border-r bg-background z-40 ${windowWidth < 768 ? "shadow-lg" : ""}`}
                    aria-label="Primary navigation"
                    id="site-sidebar"
                >
                    <SidebarProvider>
                        <SidebarGroup>
                            <SidebarMenu>
                                {navItems.map((item) => {
                                    const isActive = currentPath.startsWith(item.url)

                                    return (
                                        <SidebarMenuItem key={item.title}>
                                            <SidebarMenuButton asChild>
                                                <Link
                                                    href={item.url}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                                                        isActive
                                                            ? "bg-accent text-accent-foreground shadow-sm"
                                                            : "text-muted-foreground hover:bg-muted/50"
                                                    }`}
                                                    aria-current={isActive ? "page" : undefined}
                                                >
                                                    <item.icon className="h-4 w-4" aria-hidden="true" />
                                                    <span>{item.title}</span>
                                                </Link>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    )
                                })}

                                <Separator />

                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <Link
                                            href="/sign_out"
                                            method="delete"
                                            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-red-50"
                                        >
                                            <LogOut className="h-4 w-4" aria-hidden="true" />
                                            <span>Odjavi se</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </SidebarMenu>
                        </SidebarGroup>
                    </SidebarProvider>
                </aside>
            )}

            <main className={`pt-20 px-4 md:px-6 transition-all ${sidebarOpen ? "md:ml-64" : "md:ml-0"}`}>
                {children}
            </main>
        </div>
    )
}
